import * as React from 'react';
import { cn } from '@/lib/cn';
import { Input, type InputProps } from './input';
import { Label } from './label';

export interface TextFieldProps extends InputProps {
    /** Label visible arriba del input. */
    label: string;
    /** Texto helper debajo del input (gris). Se oculta si hay error. */
    helperText?: string;
    /** Mensaje de error (rojo). Si está presente, sobrescribe `helperText` y marca el input como inválido. */
    error?: string;
    /** Clase adicional para el wrapper div. */
    containerClassName?: string;
}

/**
 * Campo de texto reutilizable: Label + Input + helper/error en una sola unidad.
 *
 * - Usá `error` para mostrar mensaje de validación (rojo).
 * - Usá `helperText` para hint informativo (gris). Se oculta si hay error.
 * - El input recibe automáticamente `aria-invalid` cuando hay error.
 * - El id se genera con `React.useId()` si no se pasa; el label apunta a él.
 */
export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
    ({ label, helperText, error, id, className, containerClassName, ...inputProps }, ref) => {
        const generatedId = React.useId();
        const inputId = id ?? generatedId;
        const hasError = !!error;
        const messageId = `${inputId}-msg`;

        return (
            <div className={cn('space-y-1.5', containerClassName)}>
                <Label htmlFor={inputId} className={cn(hasError && 'text-destructive')}>
                    {label}
                </Label>
                <Input
                    ref={ref}
                    id={inputId}
                    aria-invalid={hasError}
                    aria-describedby={(error ?? helperText) ? messageId : undefined}
                    className={cn(
                        hasError && 'border-destructive focus-visible:ring-destructive/40',
                        className,
                    )}
                    {...inputProps}
                />
                {(error ?? helperText) && (
                    <p
                        id={messageId}
                        className={cn(
                            'text-xs',
                            hasError ? 'text-destructive' : 'text-muted-foreground',
                        )}
                    >
                        {error ?? helperText}
                    </p>
                )}
            </div>
        );
    },
);
TextField.displayName = 'TextField';
